/* The model's commentary, rendered as the markdown it is written in.

   The generator asks for two things: the file, and then prose saying what the
   model decided and what it filled in for the teacher. The second half is
   written in markdown because that is how a model writes prose — and it was
   being shown as plain text, so a heading arrived as `## Co jsem doplnil` and
   emphasis arrived with its asterisks. That reads as a typo, which is the one
   impression this half must not give: it is the part a teacher is meant to
   trust and act on.

   The static portal rendered it; the rewrite dropped the renderer and nobody
   noticed, because the sample stream the tests use has no markdown in it. This
   is that renderer, ported.

   Two things are deliberately absent.

   **No fenced code.** It cannot get here: api/src/generator.ts sorts the answer
   into two channels by the fence, so anything between ``` and ``` has already
   gone to the file. A fence in this text would mean the splitter was wrong, and
   drawing it as code would hide that.

   **Italics are not in inline().** inline() sets every prose field in the
   material, where a lone asterisk in a hand-written sentence is a stray
   character and not an instruction. Here it is an instruction. That is the
   whole reason this file exists rather than a flag on the block renderer. */

import type { ReactNode } from "react";
import { inline } from "./inline";

const HEADING = /^ {0,3}(#{1,6})\s+(.+?)\s*#*\s*$/;
const BULLET = /^ {0,3}[-*+]\s+(.+)$/;
const NUMBERED = /^ {0,3}\d+[.)]\s+(.+)$/;

type List = { ordered: boolean; items: string[] };

export function markdown(text: string, hrefOf: (path: string) => string): ReactNode[] {
  const out: ReactNode[] = [];
  const lines = String(text ?? "").replace(/\r/g, "").split("\n");
  let para: string[] = [];
  let list: List | null = null;

  const endPara = () => {
    if (!para.length) return;
    /* Joined, not broken into <br>: the paragraph keeps its newlines and the
       stylesheet is told to honour them. A model wraps its lines where it
       likes and the wrapping is not meaning, but neither is throwing it away. */
    out.push(<p key={out.length}>{inline(para.join("\n"), hrefOf, true)}</p>);
    para = [];
  };

  const endList = () => {
    if (list === null) return;
    const items = list.items.map((item, i) => <li key={i}>{inline(item, hrefOf, true)}</li>);
    out.push(list.ordered ? <ol key={out.length}>{items}</ol> : <ul key={out.length}>{items}</ul>);
    list = null;
  };

  const flush = () => { endPara(); endList(); };

  for (const line of lines) {
    if (!line.trim()) { flush(); continue; }

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      /* One level of heading, whatever the model asked for. This sits inside a
         page that already has its own hierarchy, and a commentary that opens
         with an <h1> would outrank the material it is commenting on. */
      out.push(<h4 key={out.length}>{inline(heading[2]!, hrefOf, true)}</h4>);
      continue;
    }

    /* A bullet is looked for before emphasis is: a line opening with "* " is a
       list item, and reading it as an unclosed italic is how a list turns into
       a paragraph full of asterisks. */
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      endPara();
      const ordered = numbered !== null;
      if (list === null || list.ordered !== ordered) {
        endList();
        list = { ordered, items: [] };
      }
      list.items.push((bullet ?? numbered)![1]!);
      continue;
    }

    endList();
    para.push(line);
  }

  flush();
  return out;
}
